import { useEffect, useState, useCallback } from "react";
import { Inbox as InboxIcon, Send, FileText, Trash2, AlertOctagon, RefreshCw, Reply, Archive, Paperclip, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FOLDERS = [
  { key: "INBOX", label: "Posta in arrivo", icon: InboxIcon },
  { key: "SENT", label: "Inviata", icon: Send },
  { key: "DRAFT", label: "Bozze", icon: FileText },
  { key: "SPAM", label: "Spam", icon: AlertOctagon },
  { key: "TRASH", label: "Cestino", icon: Trash2 },
];

function fmt(ts) {
  if (!ts) return "";
  try { return new Date(Number(ts)).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

export default function Inbox() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState("");
  const [label, setLabel] = useState("INBOX");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/inbox/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { label, page: 1, page_size: 40 };
    if (account) params.account_token_id = account;
    if (q) params.q = q;
    api.get("/inbox", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [label, account, q]);

  useEffect(() => { load(); }, [load]);

  const openEmail = async (it) => {
    try {
      const { data } = await api.get(`/inbox/${it.id}`);
      setOpen(data); setReplyBody("");
      if (it.is_unread) { api.post(`/inbox/${it.id}/read`, { read: true }).then(() => load()).catch(() => {}); }
    } catch { toast.error("Impossibile aprire l'email"); }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/inbox/${id}/${action}`);
      toast.success(action === "archive" ? "Archiviata" : action === "trash" ? "Spostata nel cestino" : "Fatto");
      setOpen(null); load();
    } catch (e) { toast.error("Operazione non riuscita"); }
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !open) return;
    setSending(true);
    try {
      await api.post(`/inbox/${open.id}/reply`, { body: replyBody });
      toast.success("Risposta inviata");
      setReplyBody(""); setOpen(null); load();
    } catch (e) { toast.error("Invio non riuscito"); } finally { setSending(false); }
  };

  return (
    <div className="max-w-[1200px] flex flex-col gap-4" data-testid="inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-ink flex items-center gap-2">
          <InboxIcon className="w-6 h-6 text-brand-dark" strokeWidth={1.5} /> Posta
        </h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="inbox-account-filter" value={account} onChange={(e) => setAccount(e.target.value)} className="text-sm bg-white border border-line rounded-[6px] px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand">
            <option value="">{user?.role === "admin" ? "Tutte le caselle" : "Le mie caselle"}</option>
            {accounts.map((a) => (
              <option key={a.token_id} value={a.token_id}>{a.account_email}{user?.role === "admin" ? ` · ${a.owner_name || "—"}` : ""}</option>
            ))}
          </select>
          <input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca oggetto/mittente" className="text-sm bg-white border border-line rounded-[6px] px-3 py-1.5 w-56 focus:outline-none focus:ring-2 focus:ring-brand" />
          <button data-testid="inbox-refresh" onClick={load} className="flex items-center gap-1.5 text-sm text-subtle hover:text-ink transition-colors"><RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Aggiorna</button>
        </div>
      </div>

      {accounts.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-[8px] px-4 py-3 text-sm text-amber-800">
          Nessuna casella Google collegata visibile. Collega un account dal Profilo (gli account già collegati vanno <b>ricollegati una volta</b> per abilitare la lettura della posta).
        </div>
      )}
      {accounts.some((a) => a.scope_missing) && (
        <div data-testid="inbox-reconnect-banner" className="bg-amber-50 border border-amber-200 rounded-[8px] px-4 py-3 text-sm text-amber-800">
          Per leggere la posta serve un permesso aggiuntivo. <b>Ricollega</b> queste caselle (dal Profilo o da Amministrazione → Utenti → “Collega Google”): {accounts.filter((a) => a.scope_missing).map((a) => a.account_email).join(", ")}.
        </div>
      )}

      <div className="flex gap-4">
        {/* Folders */}
        <div className="w-48 shrink-0 flex flex-col gap-1">
          {FOLDERS.map((f) => (
            <button key={f.key} data-testid={`folder-${f.key}`} onClick={() => { setLabel(f.key); setOpen(null); }}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-[6px] text-sm font-medium transition-colors ${label === f.key ? "bg-brand-soft text-brand-dark" : "text-subtle hover:bg-background"}`}>
              <f.icon className="w-4 h-4" strokeWidth={1.5} /> {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="flex-1 min-w-0 bg-white border border-line rounded-[8px] overflow-hidden">
          <div className="px-4 py-2 border-b border-line text-xs text-subtle">{total} messaggi</div>
          {loading ? (
            <p className="p-6 text-sm text-subtle">Caricamento…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-subtle">Nessun messaggio in questa cartella.</p>
          ) : (
            <div className="divide-y divide-line max-h-[70vh] overflow-y-auto">
              {items.map((it) => (
                <button key={it.id} data-testid={`email-row-${it.id}`} onClick={() => openEmail(it)}
                  className={`w-full text-left px-4 py-3 hover:bg-background transition-colors flex items-start gap-3 ${it.is_unread ? "bg-brand-soft/20" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm truncate ${it.is_unread ? "font-semibold text-ink" : "text-ink"}`}>{label === "SENT" ? it.to : (it.from_name || it.from_email)}</p>
                      {it.has_attachments && <Paperclip className="w-3 h-3 text-faint shrink-0" strokeWidth={1.5} />}
                    </div>
                    <p className={`text-sm truncate ${it.is_unread ? "font-medium text-ink" : "text-subtle"}`}>{it.subject || "(nessun oggetto)"}</p>
                    <p className="text-xs text-faint truncate">{it.snippet}</p>
                    {user?.role === "admin" && <p className="text-[11px] text-faint mt-0.5">{it.account_email} · {it.owner_name}</p>}
                  </div>
                  <span className="text-[11px] text-faint shrink-0">{fmt(it.internal_ts)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Email detail modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div data-testid="email-detail" className="bg-white rounded-[10px] w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-line flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-ink">{open.subject || "(nessun oggetto)"}</h3>
                <p className="text-xs text-subtle mt-1">Da: {open.from_name ? `${open.from_name} <${open.from_email}>` : open.from_email}</p>
                <p className="text-xs text-subtle">A: {open.to}</p>
                <p className="text-[11px] text-faint mt-0.5">{open.account_email} · {fmt(open.internal_ts)}</p>
              </div>
              <button data-testid="email-close" onClick={() => setOpen(null)} className="text-faint hover:text-ink"><X className="w-5 h-5" strokeWidth={1.5} /></button>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {open.body_html ? (
                <div className="prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: open.body_html }} />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-ink font-sans">{open.body_text}</pre>
              )}
              {open.attachments?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {open.attachments.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-background border border-line rounded-[6px] px-2 py-1"><Paperclip className="w-3 h-3" strokeWidth={1.5} />{a.filename}</span>
                  ))}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-line flex flex-col gap-2">
              <textarea data-testid="reply-body" value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={3} placeholder={`Rispondi a ${open.from_email}…`} className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button data-testid="email-archive" onClick={() => doAction(open.id, "archive")} title="Archivia" className="flex items-center gap-1 text-xs text-subtle hover:text-ink px-2 py-1.5 rounded-[6px] hover:bg-background transition-colors"><Archive className="w-4 h-4" strokeWidth={1.5} /> Archivia</button>
                  <button data-testid="email-trash" onClick={() => doAction(open.id, "trash")} title="Cestina" className="flex items-center gap-1 text-xs text-destructive hover:opacity-80 px-2 py-1.5 rounded-[6px] hover:bg-background transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /> Cestina</button>
                </div>
                <button data-testid="reply-send" onClick={sendReply} disabled={sending || !replyBody.trim()} className="flex items-center gap-1.5 bg-brand text-ink font-medium rounded-[6px] px-4 py-2 text-sm hover:bg-brand-hover disabled:opacity-50 transition-colors">
                  <Reply className="w-4 h-4" strokeWidth={1.5} /> {sending ? "Invio…" : "Rispondi"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
